<template>
  <b-modal
    id="renew-subscription-modal"
    ref="modal"
    title="Renew Subscription"
    size="lg"
    :ok-title="submitting ? 'Renewing…' : 'Renew'"
    :ok-disabled="submitting || !form.payment_method"
    @ok="handleSubmit"
    @hidden="resetForm"
  >
    <div v-if="subscription">
      <!-- Subscription summary -->
      <b-card class="mb-3" bg-variant="light">
        <div class="d-flex justify-content-between">
          <div>
            <h6 class="mb-1">{{ subscription.customer.full_name || subscription.customer.name }}</h6>
            <p class="mb-1 text-muted"><strong>Package:</strong> {{ subscription.package.name }}</p>
            <p class="mb-0 text-muted"><strong>Current End Date:</strong> {{ formatDate(subscription.end_date) }}</p>
          </div>
          <div class="text-right">
            <span class="badge" :class="subscription.status === 'active' ? 'badge-success' : 'badge-secondary'">
              {{ subscription.status }}
            </span>
          </div>
        </div>
      </b-card>

      <b-form>
        <!-- Participants -->
        <!-- <b-form-group label="Participants">
          <div class="d-flex align-items-center">
            <b-button size="sm" variant="outline-secondary" :disabled="form.participant_count <= 1"
              @click="form.participant_count = Math.max(1, form.participant_count - 1)">−</b-button>
            <span class="mx-3 font-weight-bold">{{ form.participant_count }}</span>
            <b-button size="sm" variant="outline-secondary"
              @click="form.participant_count++">+</b-button>
          </div>
        </b-form-group> -->

        <!-- Discount (only if user has permission) -->
        <div v-if="canDiscount">
          <b-form-row>
            <b-col cols="12" sm="4">
              <b-form-group label="Discount Type">
                <b-form-select v-model="form.discount_type" :options="discountTypes" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="4">
              <b-form-group label="Discount Value">
                <b-form-input
                  v-model.number="form.discount_value"
                  type="number"
                  min="0"
                  :placeholder="form.discount_type === 'pct' ? '0–100' : '0.00'"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="4">
              <b-form-group label="Discount Reason">
                <b-form-input v-model="form.discount_reason" placeholder="Optional" />
              </b-form-group>
            </b-col>
          </b-form-row>
        </div>

        <!-- Financial Preview -->
        <b-card class="mb-3 border-0" bg-variant="light">
          <div class="d-flex justify-content-between text-muted mb-1" style="font-size:13px">
            <span>{{ form.participant_count }} × KES {{ formatAmount(subscription.package.price) }}</span>
            <span>KES {{ formatAmount(grossSubtotal) }}</span>
          </div>
          <div v-if="discountPreview > 0" class="d-flex justify-content-between text-success mb-1" style="font-size:13px">
            <span>Discount<em v-if="form.discount_reason" class="ml-1 text-muted">({{ form.discount_reason }})</em></span>
            <span>− KES {{ formatAmount(discountPreview) }}</span>
          </div>
          <hr class="my-2">
          <div class="d-flex justify-content-between font-weight-bold">
            <span>Amount Due</span>
            <span>KES {{ formatAmount(amountDue) }}</span>
          </div>
        </b-card>

        <!-- Payment Method -->
        <b-form-group label="Payment Method *">
          <b-form-select
            v-model="form.payment_method"
            :options="paymentMethodOptions"
            :disabled="loadingPaymentMethods"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                {{ loadingPaymentMethods ? 'Loading…' : 'Select payment method' }}
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <!-- Payment Amount (partial payment support) -->
        <b-form-group label="Amount to Pay Now (KES)">
          <b-form-input
            v-model.number="form.payment_amount"
            type="number"
            min="0"
            :max="amountDue"
            :placeholder="formatAmount(amountDue)"
          />
          <small v-if="form.payment_amount < amountDue && form.payment_amount >= 0" class="text-warning">
            <i class="bi bi-exclamation-triangle mr-1"></i>
            Partial payment — KES {{ formatAmount(amountDue - form.payment_amount) }} will remain as balance.
          </small>
          <small v-if="form.payment_amount === 0" class="text-muted">
            Renewal will be recorded without immediate payment.
          </small>
        </b-form-group>

        <!-- Wallet -->
        <b-card bg-variant="light" class="mb-3">
          <div class="d-flex flex-wrap justify-content-between align-items-center">
            <div class="mr-2">
              <strong>Wallet Balance:</strong> KES {{ formatAmount(walletBalance) }}
            </div>
            <b-form-checkbox v-model="form.use_wallet" :disabled="walletBalance <= 0">
              Use wallet balance
            </b-form-checkbox>
          </div>
        </b-card>

        <!-- New period preview -->
        <b-alert show variant="info" class="mb-3">
          <i class="bi bi-calendar-range mr-1"></i>
          New period: <strong>{{ newStartDate }}</strong> → <strong>{{ newEndDate }}</strong>
        </b-alert>

        <!-- Notes -->
        <b-form-group label="Renewal Note (optional)">
          <b-form-textarea
            v-model="form.notes"
            rows="2"
            placeholder="Add a note for this renewal…"
          />
        </b-form-group>
      </b-form>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'RenewSubscriptionModal',

  props: {
    canDiscount: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      subscription: null,
      submitting: false,
      loadingPaymentMethods: false,
      walletBalance: 0,
      paymentMethods: [],
      form: {
        payment_method:  null,
        use_wallet:      false,
        payment_amount:  null,
        participant_count: 1,
        discount_type:   'flat',
        discount_value:  0,
        discount_reason: '',
        notes:           '',
      },
      discountTypes: [
        { value: 'flat', text: 'Flat (KES)' },
        { value: 'pct',  text: 'Percent (%)' },
      ],
    }
  },

  computed: {
    paymentMethodOptions() {
      return this.paymentMethods.map(m => ({
        value: m.id,
        text: m.payment_method + (m.account_label ? ' · ' + m.account_label : m.account_number ? ' · ' + m.account_number : ''),
      }))
    },

    grossSubtotal() {
      if (!this.subscription) return 0
      return parseFloat(this.subscription.package.price || 0) * (this.form.participant_count || 1)
    },

    discountPreview() {
      const v = parseFloat(this.form.discount_value || 0)
      if (this.form.discount_type === 'pct') {
        const pct = Math.min(100, Math.max(0, v))
        return Math.round((this.grossSubtotal * pct / 100) * 100) / 100
      }
      return Math.min(this.grossSubtotal, Math.max(0, v))
    },

    amountDue() {
      return Math.max(0, this.grossSubtotal - this.discountPreview)
    },

    newStartDate() {
      if (!this.subscription) return ''
      const end = new Date(this.subscription.end_date)
      const today = new Date()
      const base = end < today ? today : new Date(end.getTime() + 86400000)
      return base.toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' })
    },

    newEndDate() {
      if (!this.subscription) return ''
      const end = new Date(this.subscription.end_date)
      const today = new Date()
      const base = end < today ? today : new Date(end.getTime() + 86400000)
      const pkg = this.subscription.package
      const d = new Date(base)
      switch (pkg.duration_unit) {
        case 'day':   d.setDate(d.getDate() + pkg.duration_value); break
        case 'week':  d.setDate(d.getDate() + pkg.duration_value * 7); break
        case 'month': d.setMonth(d.getMonth() + pkg.duration_value); break
        case 'year':  d.setFullYear(d.getFullYear() + pkg.duration_value); break
      }
      return d.toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' })
    },
  },

  methods: {
    async show(subscription) {
      this.subscription = subscription
      this.form.participant_count = subscription.participant_count || 1
      this.form.payment_amount    = null  // defaults to full amount_due in backend
      await Promise.all([
        this.loadPaymentMethods(),
        this.loadWalletBalance(subscription.customer_id),
      ])
      this.$refs.modal.show()
    },

    async loadPaymentMethods() {
      if (this.paymentMethods.length) return
      this.loadingPaymentMethods = true
      try {
        const { data } = await axios.get('/api/payment-methods')
        this.paymentMethods = data.data || data
      } catch (e) {
        console.error('Failed to load payment methods', e)
      } finally {
        this.loadingPaymentMethods = false
      }
    },

    async loadWalletBalance(customerId) {
      try {
        const { data } = await axios.get(`/api/subscriptions/wallet/${customerId}`)
        this.walletBalance = data.balance || 0
      } catch {
        this.walletBalance = 0
      }
    },

    async handleSubmit(event) {
      event.preventDefault()
      this.submitting = true
      try {
        const payload = {
          payment_method:    this.form.payment_method,
          use_wallet:        this.form.use_wallet,
          participant_count: this.form.participant_count,
          notes:             this.form.notes || undefined,
        }

        if (this.form.payment_amount !== null && this.form.payment_amount !== '') {
          payload.payment_amount = parseFloat(this.form.payment_amount) || 0
        }

        if (this.canDiscount && this.form.discount_value > 0) {
          payload.discount_type   = this.form.discount_type
          payload.discount_value  = this.form.discount_value
          payload.discount_reason = this.form.discount_reason || undefined
        }

        const { data } = await axios.post(
          `/api/subscriptions/${this.subscription.id}/renew`,
          payload
        )

        this.$bvToast.toast('Subscription renewed successfully', {
          title: 'Success', variant: 'success',
        })
        this.$emit('renewed', data.data)
        this.$refs.modal.hide()
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to renew subscription', {
          title: 'Error', variant: 'danger',
        })
      } finally {
        this.submitting = false
      }
    },

    formatDate(date) {
      return date ? moment(date).format('DD MMM YYYY') : '—'
    },

    formatAmount(val) {
      return parseFloat(val || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    resetForm() {
      this.subscription = null
      this.walletBalance = 0
      this.form = {
        payment_method:    null,
        use_wallet:        false,
        payment_amount:    null,
        participant_count: 1,
        discount_type:     'flat',
        discount_value:    0,
        discount_reason:   '',
        notes:             '',
      }
    },
  },
}
</script>
